import React, { useState } from "react";
import { Check, Circle, ChevronDownIcon } from "lucide-react";
import { TaskStatus } from "tekkr-common/dist/model/project/project";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { cn } from "../../../lib/utils";

const DotIcon = ({ className }: { className?: string }) => (
    <Circle className={className} fill="currentColor" strokeWidth={0} />
);

interface StatusOption {
    value: TaskStatus | null;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    iconClassName: string;
    textClassName: string;
    bgClassName: string;
    borderClassName: string;
    hoverBgClassName: string;
}

const STATUS_OPTIONS: StatusOption[] = [
    {
        value: null,
        label: "Todo",
        icon: Circle,
        iconClassName: "text-foreground/40",
        textClassName: "",
        bgClassName: "",
        borderClassName: "border border-border",
        hoverBgClassName: "hover:bg-accent",
    },
    {
        value: "started",
        label: "Started",
        icon: DotIcon,
        iconClassName: "text-status-started",
        textClassName: "text-status-started-text",
        bgClassName: "bg-status-started-bg",
        borderClassName: "border border-status-started-border",
        hoverBgClassName: "hover:bg-status-started-bg",
    },
    {
        value: "on_hold",
        label: "On hold",
        icon: DotIcon,
        iconClassName: "text-status-on-hold",
        textClassName: "text-status-on-hold-text",
        bgClassName: "bg-status-on-hold-bg",
        borderClassName: "border border-status-on-hold-border",
        hoverBgClassName: "hover:bg-status-on-hold-bg",
    },
    {
        value: "done",
        label: "Done",
        icon: Check,
        iconClassName: "text-status-done",
        textClassName: "text-status-done-text",
        bgClassName: "bg-status-done-bg",
        borderClassName: "border border-status-done-border",
        hoverBgClassName: "hover:bg-status-done-bg",
    },
];

interface TaskStatusSelectorProps {
    status: TaskStatus | null;
    onStatusChange: (status: TaskStatus | null) => void;
    availableStatuses?: Array<TaskStatus | null>;
}

export function TaskStatusSelector({ status, onStatusChange, availableStatuses }: TaskStatusSelectorProps) {
    const [open, setOpen] = useState(false);
    const options = availableStatuses
        ? STATUS_OPTIONS.filter((o) => availableStatuses.includes(o.value))
        : STATUS_OPTIONS;
    const current = options.find((o) => o.value === status) ?? options[0];
    const CurrentIcon = current.icon;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div
                    className={cn(
                        "flex h-8 min-w-[6.5rem] cursor-pointer flex-row items-center justify-between whitespace-nowrap rounded-md px-2 text-sm font-medium transition-all hover:opacity-80",
                        current.bgClassName,
                        current.borderClassName,
                        current.textClassName
                    )}
                >
                    <span className="flex items-center gap-1">
                        <CurrentIcon className={cn("h-3.5 w-3.5 shrink-0", current.iconClassName)} />
                        <span>{current.label}</span>
                    </span>
                    <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </div>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
                {options.map((option) => {
                    const Icon = option.icon;
                    return (
                        <div
                            key={String(option.value)}
                            className={cn(
                                "flex cursor-pointer flex-row items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-medium transition-all",
                                option.hoverBgClassName,
                                option.textClassName
                            )}
                            onClick={() => {
                                onStatusChange(option.value);
                                setOpen(false);
                            }}
                        >
                            <Icon className={cn("h-3.5 w-3.5 shrink-0", option.iconClassName)} />
                            <span>{option.label}</span>
                        </div>
                    );
                })}
            </PopoverContent>
        </Popover>
    );
}
